import { Alert, Box, Button, TextField, Typography } from '@mui/material'
import useSendSignInLinkToEmailMutation from '@zealth/core/firebase/useSendSignInLinkToEmailMutation'
import { type ActionCodeSettings } from 'firebase/auth'
import { type FormEvent, useState } from 'react'

import { writePendingEmail } from './pendingSignInEmail'

/**
 * Continue URL on the playground's own origin, NOT on the app's link domain.
 *
 * The mobile app claims `/__/auth` and `/signin` on that domain as Universal
 * Links, so a partner opening their link on a phone with the app installed
 * would have it swallowed by the app instead of reaching the playground.
 */
const actionCodeSettings = (): ActionCodeSettings => ({
  url: `${window.location.origin}/`,
  handleCodeInApp: true,
})

const PartnerSignInForm = () => {
  const [email, setEmail] = useState('')
  const [sent, setSent] = useState(false)
  const sendLink = useSendSignInLinkToEmailMutation()

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault()
    const address = email.trim().toLowerCase()
    if (!address) return

    sendLink.mutate(
      { email: address, settings: actionCodeSettings() },
      {
        onSuccess: () => {
          writePendingEmail(address)
          setSent(true)
        },
      }
    )
  }

  if (sent) {
    return (
      <Alert severity="success">
        If that address has access, a sign-in link is on its way. Open it in
        this browser.
      </Alert>
    )
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={event => setEmail(event.target.value)}
        fullWidth
        required
        sx={{ mb: 2 }}
      />
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={sendLink.isPending}
      >
        Send sign-in link
      </Button>

      {sendLink.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {sendLink.error instanceof Error
            ? sendLink.error.message
            : 'Could not send the link.'}
        </Alert>
      )}

      {/* Deliberately says nothing about whether the address has access.
          Receiving a link grants nothing on its own — the role does — so the
          copy has no reason to confirm who exists. */}
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 2 }}
      >
        Zealth partners only. Your partner manager or Zealth has to give you
        access before a link will work.
      </Typography>
    </Box>
  )
}

export default PartnerSignInForm
